const normalize = value => value.toLocaleLowerCase('sv').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
class CCCollectionSearch extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.input = this.querySelector('input');
    this.message = this.querySelector('[role="status"]');
    this.isPokemon = this.hasAttribute('data-pokemon-filters');
    this.hasTypeFilter = this.hasAttribute('data-type-filter');
    this.hasSetFilter = this.hasAttribute('data-set-filter') || this.hasTypeFilter;
    this.selectedType = '';
    this.era = 'all';
    this.selectedSet = '';
    this.header = this.closest('.cc-collection-header');
    this.headerCount = this.header?.querySelector('.cc-collection-total');
    this.originalHeaderCount = this.headerCount?.textContent || '';
    this.onFilterClick = event => {
      const more = event.target.closest('[data-cc-filter-more]');
      if (this.hasSetFilter && more && this.root?.contains(more)) { event.preventDefault(); this.showMore(); return; }
      const eraButton = event.target.closest('[data-cc-era]');
      const reset = event.target.closest('[data-cc-filters-reset]');
      if (!this.hasSetFilter || (!eraButton && !reset)) return;
      if (eraButton && !this.header.contains(eraButton)) return;
      event.preventDefault();
      this.era = eraButton ? eraButton.dataset.ccEra : 'all';
      if (reset) { this.selectedSet = ''; this.selectedType = ''; }
      this.syncFilters();
      clearTimeout(this.timer);
      this.run();
    };
    this.onSetChange = event => {
      if (!this.hasSetFilter || !event.target.matches('[data-cc-set-select], [data-cc-type-select]')) return;
      event.stopPropagation();
      if (event.target.matches('[data-cc-type-select]')) this.selectedType = event.target.value;
      else this.selectedSet = event.target.value;
      this.syncFilters();
      clearTimeout(this.timer);
      this.run();
    };
    document.addEventListener('click', this.onFilterClick);
    document.addEventListener('change', this.onSetChange);
    this.querySelector('form').addEventListener('submit', event => { event.preventDefault(); clearTimeout(this.timer); this.run(); });
    this.input.addEventListener('input', () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.run(), 280); });
    this.querySelector('[data-clear]').addEventListener('click', () => { clearTimeout(this.timer); this.input.value = ''; this.run(); this.input.focus(); });
    this.observer = new MutationObserver(() => {
      const grid = document.querySelector('.cc-collection .product-grid');
      if (grid !== this.grid || this.context !== this.key()) {
        clearTimeout(this.refresh);
        this.refresh = setTimeout(() => { this.bind(); if (this.hasActiveFilters()) this.run(); }, 80);
      }
    });
    this.bind();
    this.observer.observe(document.querySelector('main') || document.documentElement, { childList: true, subtree: true });
  }
  disconnectedCallback() { this.observer?.disconnect(); clearTimeout(this.timer); clearTimeout(this.refresh); this.controller?.abort(); this.optionsController?.abort(); document.removeEventListener('click', this.onFilterClick); document.removeEventListener('change', this.onSetChange); }
  updateMoreButton() {
    this.root?.querySelector('[data-cc-filter-more]')?.remove();
    if (!this.hasSetFilter || !this.matches || this.shown >= this.matches.length) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.ccFilterMore = '';
    button.className = 'cc-filter-more';
    button.textContent = 'Visa fler (' + this.shown + ' av ' + this.matches.length + ')';
    this.grid.after(button);
  }
  async showMore() {
    if (this.loadingMore || !this.matches || !this.controller || this.controller.signal.aborted) return;
    this.loadingMore = true;
    const token = this.token;
    const run = { controller: this.controller, requests: 0, bytes: 0 };
    const end = Math.min(this.shown + this.pageSize, this.matches.length);
    const next = this.matches.slice(this.shown, end);
    const button = this.root.querySelector('[data-cc-filter-more]');
    if (button) { button.disabled = true; button.textContent = 'Laddar fler…'; }
    try {
      await this.loadCards(next, run);
      if (token !== this.token) return;
      this.grid.append(...next.map(item => document.importNode(this.cards.get(item.id), true)));
      this.shown = end;
      this.message.textContent = this.shown + ' av ' + this.matches.length + ' produkter visas.';
    } catch (error) {
      if (error.name !== 'AbortError' && token === this.token) this.message.textContent = 'Fler produkter kunde inte laddas. Försök igen.';
    } finally {
      this.loadingMore = false;
      if (token === this.token) this.updateMoreButton();
    }
  }
  hasActiveFilters() { return Boolean(this.input.value.trim() || (this.hasSetFilter && (this.selectedType || this.selectedSet || (this.isPokemon && this.era !== 'all')))); }
  syncFilters() {
    if (!this.hasSetFilter) return;
    this.header.querySelectorAll('[data-cc-era]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ccEra === this.era)));
    document.querySelectorAll('[data-cc-set-select]').forEach(select => {
      if (this.stockIndex) {
        const sets = [...new Set(this.stockIndex.map(item => item.set).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'sv', { numeric: true }));
        const options = [new Option('Alla set', '')];
        sets.forEach(set => options.push(new Option(set, set)));
        if (this.selectedSet && !sets.includes(this.selectedSet)) options.push(new Option(this.selectedSet + ' (inga i lager)', this.selectedSet));
        select.replaceChildren(...options);
        select.disabled = false;
      }
      select.value = this.selectedSet;
    });
    this.querySelectorAll('[data-cc-type-select]').forEach(select => { select.value = this.selectedType; });
    const summary = this.querySelector('[data-cc-filter-summary]');
    if (summary) summary.textContent = [this.selectedSet, this.selectedType, this.era === 'all' ? '' : this.era === 'modern' ? 'Modern' : 'Vintage'].filter(Boolean).join(' · ');
    document.querySelectorAll('[data-cc-filters-reset]').forEach(button => { button.hidden = this.era === 'all' && !this.selectedSet && !this.selectedType; });
  }
  async prepareSets() {
    if (!this.hasSetFilter) return;
    this.optionsController?.abort();
    const run = { controller: new AbortController(), requests: 0, bytes: 0 };
    this.optionsController = run.controller;
    try {
      await this.loadIndex(run, true);
      if (!run.controller.signal.aborted) this.syncFilters();
    } catch (error) {
      if (error.name !== 'AbortError') {
        document.querySelectorAll('[data-cc-set-select]').forEach(select => {
          select.replaceChildren(new Option('Set kunde inte laddas', ''));
          select.disabled = true;
        });
      }
    }
  }
  key() { const url = new URL(location.href); url.searchParams.delete('page'); return url.pathname + '?' + url.searchParams.toString(); }
  bind() {
    this.controller?.abort();
    this.matches = null;
    this.root?.querySelector('[data-cc-filter-more]')?.remove();
    this.token = (this.token || 0) + 1;
    this.root = document.querySelector('.cc-collection');
    this.grid = this.root?.querySelector('.product-grid');
    this.context = this.key();
    this.original = this.grid ? [...this.grid.children].map(card => card.cloneNode(true)) : [];
    this.originalCount = this.root?.querySelector('.products-count-wrapper')?.textContent || '';
    this.originalDrawerCount = this.root?.querySelector('.facets__see-results')?.textContent || '';
    this.pageSize = Number(this.root?.dataset.productsPerPage || 24);
    this.index = null;
    this.cards = new Map(this.original.map(card => [card.dataset.productId, card]));
    this.loadedPages = new Set(this.original.map(card => Number(card.dataset.page)));
    this.message.textContent = '';
    this.syncFilters();
    this.prepareSets();
  }
  updateResultCount(count = null) {
    this.root.querySelectorAll('.products-count-wrapper').forEach(element => {
      element.textContent = count === null ? this.originalCount : count + (count === 1 ? ' träff' : ' träffar');
    });
    if (!this.hasSetFilter) return;
    if (this.headerCount) this.headerCount.textContent = count === null ? this.originalHeaderCount : count + (count === 1 ? ' produkt' : ' produkter');
    this.root.querySelectorAll('.facets__see-results').forEach(button => {
      button.textContent = count === null ? this.originalDrawerCount : 'Se ' + count + ' artiklar';
    });
  }
  setSearching(active) {
    this.root.classList.toggle('cc-search-active', active);
    const observer = this.root.infinityScrollObserver;
    if (active) observer?.disconnect();
    else this.root.querySelectorAll('[ref="viewMorePrevious"], [ref="viewMoreNext"]').forEach(el => observer?.observe(el));
  }
  async request(section, page, run, inventoryOnly = false) {
    const url = new URL(this.context, location.origin);
    if (inventoryOnly) {
      [...url.searchParams.keys()].filter(key => key.startsWith('filter.')).forEach(key => url.searchParams.delete(key));
      url.searchParams.set('filter.v.availability', '1');
    }
    url.searchParams.set('section_id', section);
    url.searchParams.set('page', String(page));
    const response = await fetch(url, { signal: run.controller.signal });
    if (!response.ok) throw new Error('Collection could not be loaded');
    const html = await response.text();
    run.requests++;
    run.bytes += new TextEncoder().encode(html).length;
    return new DOMParser().parseFromString(html, 'text/html');
  }
  async loadIndex(run, inventoryOnly = false) {
    const cacheKey = inventoryOnly ? 'stockIndex' : 'index';
    if (this[cacheKey]) return this[cacheKey];
    const parse = doc => {
      const data = doc.querySelector('[data-cc-collection-index]');
      if (!data) throw new Error('Search index missing');
      return JSON.parse(data.textContent);
    };
    const first = parse(await this.request('cc-collection-search-index', 1, run, inventoryOnly));
    const output = [first.products];
    for (let page = 2; page <= first.pages; page++) output.push(parse(await this.request('cc-collection-search-index', page, run, inventoryOnly)).products);
    if (run.controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
    this[cacheKey] = output.flat().map(item => ({ ...item, text: normalize(item.text), year: Number(item.year) || 0, set: item.set || '' }));
    return this[cacheKey];
  }
  async loadCards(matches, run) {
    const pages = [...new Set(matches.filter(item => !this.cards.has(item.id)).map(item => Math.floor(item.position / this.pageSize) + 1))];
    let next = 0;
    const worker = async () => {
      while (next < pages.length) {
        const page = pages[next++];
        const doc = await this.request(this.root.getAttribute('section-id'), page, run);
        if (run.controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        doc.querySelectorAll('.product-grid > li').forEach(card => this.cards.set(card.dataset.productId, card));
        this.loadedPages.add(page);
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, pages.length) }, worker));
    if (matches.some(item => !this.cards.has(item.id))) throw new Error('Search results changed; retry');
  }
  async run() {
    const query = normalize(this.input.value);
    const token = this.token = (this.token || 0) + 1;
    this.controller?.abort();
    this.querySelector('[data-clear]').hidden = !query;
    this.matches = null;
    this.root?.querySelector('[data-cc-filter-more]')?.remove();
    if (!this.grid) { this.message.textContent = query ? 'Inga produkter i denna kollektion.' : ''; return; }
    const active = Boolean(query || (this.hasSetFilter && (this.selectedType || this.selectedSet || (this.isPokemon && this.era !== 'all'))));
    if (!active) {
      this.removeAttribute('aria-busy');
      this.grid.replaceChildren(...this.original.map(card => card.cloneNode(true)));
      this.setSearching(false);
      this.updateResultCount();
      this.message.textContent = '';
      return;
    }
    const run = { controller: new AbortController(), requests: 0, bytes: 0, started: performance.now() };
    this.controller = run.controller;
    this.setSearching(true);
    this.message.textContent = query ? 'Söker i kollektionen…' : 'Filtrerar produkter…';
    this.setAttribute('aria-busy', 'true');
    try {
      const index = await this.loadIndex(run);
      const words = query ? query.split(/\s+/) : [];
      const matches = index.filter(item => {
        if (!words.every(word => item.text.includes(word))) return false;
        if (this.hasTypeFilter && this.selectedType && item.type !== this.selectedType) return false;
        if (this.hasSetFilter && this.selectedSet && item.set !== this.selectedSet) return false;
        if (!this.isPokemon) return true;
        if (this.era === 'modern') return item.year >= 2018;
        if (this.era === 'vintage') return item.year > 0 && item.year <= 2017;
        return true;
      });
      const visible = this.hasSetFilter ? matches.slice(0, this.pageSize) : matches;
      await this.loadCards(visible, run);
      if (token !== this.token) return;
      this.matches = matches;
      this.shown = visible.length;
      this.grid.replaceChildren(...visible.map(item => document.importNode(this.cards.get(item.id), true)));
      this.message.textContent = matches.length ? matches.length + (matches.length === 1 ? ' produkt matchar' : ' produkter matchar') + (query ? ' din sökning.' : ' dina filter.') : 'Inga träffar. Prova ett annat namn eller färre filter.';
      this.updateMoreButton();
      this.updateResultCount(matches.length);
    } catch (error) {
      if (error.name !== 'AbortError' && token === this.token) this.message.textContent = 'Sökningen kunde inte laddas. Försök igen.';
    } finally {
      if (token === this.token) {
        this.removeAttribute('aria-busy');
        this.dataset.searchMs = String(Math.round(performance.now() - run.started));
        this.dataset.searchRequests = String(run.requests);
        this.dataset.searchBytes = String(run.bytes);
        this.dataset.searchIndexProducts = String(this.index?.length || 0);
      }
    }
  }
}
if (!customElements.get('cc-collection-search')) customElements.define('cc-collection-search', CCCollectionSearch);

